import { z } from 'zod';
import { baseModelSchema } from '../../baseModel';
import { IOutletFormValues } from './outletFormValues';

/**
 * Zod schema for validating outlet data.
 * Extends the base model schema with outlet-specific fields.
 * Represents a point of sale or distribution outlet in the system.
 *
 * @property {string} code - Unique identifier code (max 5 characters)
 * @property {string} [deviceMacAddress] - Optional MAC address of associated device in format XX:XX:XX:XX:XX:XX
 * @property {number} packLineId - ID of the pack line this outlet belongs to
 * @property {number} [defaultPrintServerId] - Optional ID of the default print server
 * @property {number} [defaultPrinterId] - Optional ID of the default printer
 * @property {number} [defaultReportId] - Optional ID of the default report
 * @property {number} [defaultExporterSiteId] - Optional ID of the default exporter site
 * @property {number} [defaultInventoryId] - Optional ID of the default inventory
 */
export const outletSchema = baseModelSchema.extend({
    code: z.string().nonempty('Code is required').max(5, 'Code can only be up to 5 characters long.').default(''),
    deviceMacAddress: z.string()
        .regex(
            /^([0-9A-Fa-f]{2}[:]){5}([0-9A-Fa-f]{2})$/,
            'MAC address must be in format XX:XX:XX:XX:XX:XX'
        )
        .transform(val => val.toUpperCase())
        .optional()
        .nullable(),
    packLineId: z.number(),
    defaultPrintServerId: z.number().optional(),
    defaultPrinterId: z.number().optional(),
    defaultReportId: z.number().optional(),
    defaultExporterSiteId: z.number().optional(),
    defaultInventoryId: z.number().optional(),
});

/**
 * TypeScript interface representing an Outlet object.
 * Generated from the Zod schema for type safety.
 */
export type IOutlet = z.infer<typeof outletSchema>;

/**
 * Creates a validated Outlet object from form values.
 *
 * @param {IOutletFormValues} formValues - The form data to convert
 * @returns {IOutlet} A validated outlet object
 * @throws {ZodError} If validation fails
 */
export function Outlet(formValues : IOutletFormValues) : IOutlet {
    const {
        packLine,
        defaultPrintServer,
        defaultPrinter,
        defaultReport,
        defaultExporterSite,
        defaultInventory,
    } = formValues;

    return outletSchema.parse({
        ...formValues,
        packLineId: packLine?.value ? Number(packLine.value) : undefined,
        defaultPrintServerId: defaultPrintServer?.value ? Number(defaultPrintServer.value) : undefined,
        defaultPrinterId: defaultPrinter?.value ? Number(defaultPrinter.value) : undefined,
        defaultReportId: defaultReport?.value ? Number(defaultReport.value) : undefined,
        defaultExporterSiteId: defaultExporterSite?.value ? Number(defaultExporterSite.value) : undefined,
        defaultInventoryId: defaultInventory?.value ? Number(defaultInventory.value) : undefined,
    });
}
